import { AddButton } from "@/components/common/AddButton";
import { BackdropIndicator } from "@/components/common/BackdropIndicator";
import {
  completeBinaryFileUpload,
  getBinaryFiles,
  initializeBinaryFileUpload,
  uploadBinaryFileChunk,
} from "@/components/sleep_checkup_v1/BinaryFileAPI";
import { SleepCheckupListLink } from "@/components/sleep_checkup_v1/BreadcrumbLink";
import { PAGE_TITLE } from "@/components/sleep_checkup_v1/Path";
import { SCDialogTitle } from "@/components/sleep_checkup_v1/SCDialogTitle";
import { SCSnackbar } from "@/components/sleep_checkup_v1/SCSnackbar";
import {
  Pagination,
  SCEmptyTable,
  SCTable,
} from "@/components/sleep_checkup_v1/SCTable";
import { SnackbarContext } from "@/components/sleep_checkup_v1/SnackbarContext";
import { BinaryFile } from "@/components/sleep_checkup_v1/Types";
import { includes } from "@/utils/objects";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  IconButton,
  LinearProgress,
  Link as MUILink,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import { DropzoneState, FileRejection, useDropzone } from "react-dropzone";

// 許可される拡張子（小文字）
const ALLOWED_EXTENSIONS = [".asa", ".bin"] as const;

type MeasurementDataListProps = {
  minWidth: number;
};

export function MeasurementDataList({ minWidth }: MeasurementDataListProps) {
  const [binaryFiles, setBinaryFiles] = useState<BinaryFile[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [snackbarContext, setSnackbarContext] =
    useState<SnackbarContext | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const TABLE_HEADERS = ["ファイル名", "アップロード日時"];

  const fetchBinaryFiles = useCallback(async () => {
    setLoading(true);
    try {
      const offset = (page - 1) * rowsPerPage;
      const response = await getBinaryFiles(rowsPerPage, offset);
      setBinaryFiles(response.data.results);
      setTotalCount(response.data.count);
    } catch (error) {
      console.error("Failed to fetch binary files:", error);
      setBinaryFiles([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchBinaryFiles();
  }, [fetchBinaryFiles]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  };

  const formatDateTime = (dateString: string) => {
    try {
      return format(parseISO(dateString), "yyyy/M/d HH:mm");
    } catch {
      return "-";
    }
  };

  const handleUploadButtonClick = () => {
    setUploadDialogOpen(true);
  };

  const handleUploadDialogClose = () => {
    setUploadDialogOpen(false);
  };

  const handleUploadSuccess = async () => {
    setUploadDialogOpen(false);
    setSnackbarContext(
      new SnackbarContext("ファイルをアップロードしました", "success")
    );
    await fetchBinaryFiles();
  };

  const handleSnackbarClose = () => {
    setSnackbarContext(null);
  };

  const pagination = new Pagination(rowsPerPage, page, totalCount);

  return (
    <>
      <BackdropIndicator open={loading} />
      <BinaryFileUploadDialog
        open={uploadDialogOpen}
        onClickClose={handleUploadDialogClose}
        onSuccess={handleUploadSuccess}
      />
      <Stack spacing={6} sx={{ mx: 10, my: 6, minWidth: minWidth }}>
        <Breadcrumbs>
          <SleepCheckupListLink />
          <Typography variant="body2">
            {PAGE_TITLE["MeasurementDataList"]}
          </Typography>
        </Breadcrumbs>
        <Stack spacing={2}>
          <Typography variant="h3">
            {PAGE_TITLE["MeasurementDataList"]}
          </Typography>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
          >
            <Typography variant="body2" color="text.secondary">
              測定データをアップロードすると自動的に解析が開始されます。解析が完了次第、メールにてお知らせします。
            </Typography>
            <AddButton onClick={handleUploadButtonClick}>
              データアップロード
            </AddButton>
          </Stack>
        </Stack>
        {binaryFiles.length > 0 && (
          <SCTable<BinaryFile>
            headers={TABLE_HEADERS}
            list={binaryFiles}
            pagination={pagination}
            rowsPerPageOptions={[10, 25, 50, 100]}
            tableRow={(data, i) => {
              return (
                <TableRow key={i}>
                  <TableCell sx={{ width: "50%" }}>{data.filename}</TableCell>
                  <TableCell sx={{ width: "50%" }}>
                    {data.upload_completed_at
                      ? formatDateTime(data.upload_completed_at)
                      : "-"}
                  </TableCell>
                </TableRow>
              );
            }}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            maxHeight="60vh"
          />
        )}
        {binaryFiles.length === 0 && !loading && (
          <SCEmptyTable
            headers={TABLE_HEADERS}
            message="測定データがありません。"
          />
        )}
      </Stack>
      {snackbarContext != null && (
        <SCSnackbar
          open={true}
          severity={snackbarContext.severity}
          onClose={handleSnackbarClose}
        >
          {snackbarContext.message}
        </SCSnackbar>
      )}
    </>
  );
}

type UploadFileState = {
  file: File;
  id: string;
  progress: number;
  status: "uploading" | "completed" | "error";
  error?: string;
};

type BinaryFileUploadDialogProps = {
  open: DialogProps["open"];
  onClickClose: () => void;
  onSuccess: () => void;
};

function BinaryFileUploadDialog({
  open,
  onClickClose,
  onSuccess,
}: BinaryFileUploadDialogProps) {
  const [uploadFiles, setUploadFiles] = useState<UploadFileState[]>([]);
  const [registering, setRegistering] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const uploadFilesRef = useRef<UploadFileState[]>([]);
  const cancelledFileIdsRef = useRef<Set<string>>(new Set());
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  // uploadFilesの最新の値をrefに保存
  useEffect(() => {
    uploadFilesRef.current = uploadFiles;
  }, [uploadFiles]);

  useEffect(() => {
    if (open) {
      return;
    }

    // ダイアログが閉じられるタイミングで、stateを初期化する
    setUploadFiles([]);
    setRegistering(false);
    setCancelling(false);
    cancelledFileIdsRef.current.clear();
    abortControllersRef.current.clear();
  }, [open]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) {
      return;
    }

    // 拡張子をチェックして、許可されたファイルとそうでないファイルを分離
    // (ブラウザのMIMEタイプ判定に依存しない二重チェック)
    const validFiles: File[] = [];
    const invalidFiles: File[] = [];

    acceptedFiles.forEach((file) => {
      const extension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
      if (extension && includes(ALLOWED_EXTENSIONS, extension)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    });

    // 無効な拡張子のファイルをエラー状態で追加
    if (invalidFiles.length > 0) {
      const rejectedFiles: UploadFileState[] = invalidFiles.map((file) => {
        const tempId = `temp-${Date.now()}-${Math.random()}`;
        return {
          file,
          id: tempId,
          progress: 100,
          status: "error" as const,
          error: "ファイル形式が違います。",
        };
      });
      setUploadFiles((prev) => [...prev, ...rejectedFiles]);
    }

    if (validFiles.length === 0) {
      return;
    }

    // キャンセル状態をリセット
    setCancelling(false);

    // 受け入れたファイルを状態に追加（一時IDを付与）
    const newFiles: UploadFileState[] = validFiles.map((file) => {
      const tempId = `temp-${Date.now()}-${Math.random()}`;
      return {
        file,
        id: tempId,
        progress: 0,
        status: "uploading" as const,
      };
    });

    setUploadFiles((prev) => [...prev, ...newFiles]);

    // 各ファイルのアップロードを並列で開始
    await Promise.all(
      newFiles.map((newFile, i) => {
        const file = validFiles[i];
        const tempId = newFile.id;

        return uploadFile(file, tempId).catch((error) => {
          console.error(`Failed to upload file: ${file.name}`, error);
        });
      })
    );
  }, []);

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    // 拒否されたファイルをエラー状態でリストに追加
    const rejectedFiles: UploadFileState[] = fileRejections.map((rejection) => {
      const tempId = `temp-${Date.now()}-${Math.random()}`;
      return {
        file: rejection.file,
        id: tempId,
        progress: 100,
        status: "error" as const,
        error: "ファイル形式が違います。",
      };
    });

    setUploadFiles((prev) => [...prev, ...rejectedFiles]);
  }, []);

  const uploadFile = async (file: File, tempId: string) => {
    let fileId: string | undefined;
    const abortController = new AbortController();
    abortControllersRef.current.set(tempId, abortController);

    try {
      const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

      // 1. アップロード初期化
      const initResponse = await initializeBinaryFileUpload(
        file.name,
        file.size,
        totalChunks,
        abortController.signal
      );
      fileId = initResponse.data.id;

      // サーバーIDが取得できたらAbortControllerを更新
      abortControllersRef.current.delete(tempId);
      abortControllersRef.current.set(fileId, abortController);

      // 一時IDがキャンセルされていた場合、サーバーIDでキャンセルAPIを呼び出す
      if (cancelledFileIdsRef.current.has(tempId)) {
        cancelledFileIdsRef.current.delete(tempId);
        cancelledFileIdsRef.current.add(fileId);
        try {
          await completeBinaryFileUpload(fileId, true);
        } catch (error) {
          console.error("Failed to cancel upload:", error);
        }
        return; // キャンセル済みなので処理を終了
      }

      setUploadFiles((prev) => {
        return prev.map((f) => (f.id === tempId ? { ...f, id: fileId! } : f));
      });

      // 2. チャンクをアップロード
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        await uploadBinaryFileChunk(
          fileId,
          chunkIndex,
          chunk,
          abortController.signal
        );

        // 進捗を更新（最後のチャンク以外）
        if (chunkIndex < totalChunks - 1) {
          const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
          setUploadFiles((prev) => {
            return prev.map((f) => (f.id === fileId! ? { ...f, progress } : f));
          });
        }
      }

      // アップロード完了
      setUploadFiles((prev) => {
        return prev.map((f) =>
          f.id === fileId! ? { ...f, progress: 100, status: "completed" } : f
        );
      });
    } catch (error: any) {
      // アボートされた場合はエラー状態にしない
      if (error.name === "CanceledError" || error.name === "AbortError") {
        return;
      }

      setUploadFiles((prev) => {
        return prev.map((f) => {
          // tempIdまたはfileIdにマッチするファイルをエラー状態にする
          const isTargetFile = f.id === tempId || (fileId && f.id === fileId);
          return isTargetFile
            ? { ...f, status: "error", error: "アップロードに失敗しました" }
            : f;
        });
      });
      throw error;
    } finally {
      // AbortControllerをクリーンアップ
      abortControllersRef.current.delete(tempId);
      if (fileId) {
        abortControllersRef.current.delete(fileId);
      }
    }
  };

  const handleCloseRequest = () => {
    // ファイルがある場合は確認ダイアログを表示（登録ボタンを押していない場合）
    const hasFiles = uploadFilesRef.current.length > 0;
    const allFilesError =
      hasFiles && uploadFilesRef.current.every((f) => f.status === "error");

    // 全てのファイルがエラーの場合は確認ダイアログを表示せずに閉じる
    if (hasFiles && !allFilesError) {
      setConfirmCancelOpen(true);
    } else {
      onClickClose();
    }
  };

  const handleConfirmCancel = async () => {
    setConfirmCancelOpen(false);
    setCancelling(true);

    // すべてのアップロードを中止
    abortControllersRef.current.forEach((controller) => {
      controller.abort();
    });
    abortControllersRef.current.clear();

    // すべてのファイルをキャンセル済みとしてマーク
    uploadFilesRef.current.forEach((file) => {
      if (file.id) {
        cancelledFileIdsRef.current.add(file.id);
      }
    });

    // サーバーIDのファイルのみキャンセルAPIを呼び出す
    const filesToCancel = uploadFilesRef.current.filter(
      (f) => f.id && !String(f.id).startsWith("temp-")
    );

    try {
      await Promise.all(
        filesToCancel.map((file) => completeBinaryFileUpload(file.id, true))
      );
    } catch (error) {
      console.error("Failed to cancel uploads:", error);
    }

    // ファイルをUIから削除
    setUploadFiles([]);

    // 同じファイルを再選択できるようにinputをリセット
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleCancelConfirmDialog = () => {
    setConfirmCancelOpen(false);
  };

  const handleRegister = async () => {
    setRegistering(true);
    try {
      // 全ファイルのcomplete_uploadを実行
      await Promise.all(
        uploadFiles.map((uploadFile) => {
          if (uploadFile.id && uploadFile.status === "completed") {
            return completeBinaryFileUpload(uploadFile.id);
          }
          return Promise.resolve();
        })
      );
      onSuccess();
    } catch (error) {
      console.error("Failed to complete upload:", error);
    } finally {
      setRegistering(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive, inputRef } = useDropzone({
    onDrop,
    onDropRejected,
    noClick: true,
    multiple: true,
    accept: {
      "application/octet-stream": [".asa", ".bin"],
    },
  });

  const allFilesCompleted = uploadFiles.every((f) => f.status === "completed");
  const hasFiles = uploadFiles.length > 0;
  const canRegister =
    hasFiles &&
    allFilesCompleted &&
    !registering &&
    !confirmCancelOpen &&
    !cancelling;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseRequest}
        sx={{ "& .MuiDialog-paper": { width: "496px" } }}
      >
        <SCDialogTitle
          onClickClose={handleCloseRequest}
          noCloseButton={registering}
        >
          測定データアップロード
        </SCDialogTitle>
        <DialogContent sx={{ maxHeight: "100vh", m: 0, p: "24px" }}>
          <Stack spacing={4}>
            <Stack
              {...getRootProps()}
              sx={{
                border:
                  uploadFiles.length === 0
                    ? `1px dashed ${isDragActive ? "#0056A0" : "#E0E0E0"}`
                    : "none",
                borderRadius: "4px",
                py: 0,
                outline: "none",
              }}
              justifyContent="center"
              alignItems="center"
            >
              <input {...getInputProps()} />
              {uploadFiles.length === 0 && <FileDropzone inputRef={inputRef} />}
              {uploadFiles.length > 0 && (
                <Stack sx={{ width: "100%", gap: "10px" }}>
                  {uploadFiles.map((uploadFile, index) => (
                    <FileUploadItem
                      key={index}
                      uploadFile={uploadFile}
                      onRemove={async () => {
                        const fileId = uploadFile.id;

                        // アップロードを即座に中止
                        if (fileId) {
                          const controller =
                            abortControllersRef.current.get(fileId);
                          if (controller) {
                            controller.abort();
                            abortControllersRef.current.delete(fileId);
                          }
                          cancelledFileIdsRef.current.add(fileId);
                        }

                        // サーバーIDの場合はキャンセルAPIを呼び出す
                        if (fileId && !String(fileId).startsWith("temp-")) {
                          try {
                            await completeBinaryFileUpload(fileId, true);
                          } catch (error) {
                            console.error("Failed to cancel upload:", error);
                          }
                        }

                        setUploadFiles((prev) =>
                          prev.filter((f) => f.id !== uploadFile.id)
                        );

                        // 同じファイルを再選択できるようにinputをリセット
                        if (inputRef.current) {
                          inputRef.current.value = "";
                        }
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ pt: 4 }}>
          <AddButton onClick={handleRegister} disabled={!canRegister}>
            登録する
          </AddButton>
        </DialogActions>
      </Dialog>

      {/* 確認ダイアログ */}
      <Dialog
        open={confirmCancelOpen}
        sx={{ "& .MuiDialog-paper": { width: "432px" } }}
      >
        <SCDialogTitle onClickClose={handleCancelConfirmDialog}>
          アップロード未完了
        </SCDialogTitle>
        <DialogContent>
          <Typography>
            アップロードは完了していません。実行中のアップロードを中止しますか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirmDialog}>キャンセル</Button>
          <Button
            onClick={handleConfirmCancel}
            color="primary"
            variant="contained"
          >
            アップロードを中止
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

type FileDropzoneProps = {
  inputRef: DropzoneState["inputRef"];
};

function FileDropzone({ inputRef }: FileDropzoneProps) {
  const onClickLabel = () => {
    inputRef.current?.click();
  };

  return (
    <Stack alignItems="center" spacing={2} sx={{ py: "24px" }}>
      <UploadFileIcon sx={{ width: 40, height: 40, color: "text.secondary" }} />
      <Stack direction="row" spacing={1}>
        <MUILink
          onClick={onClickLabel}
          variant="subtitle1"
          color="primary"
          fontWeight={400}
          sx={{ cursor: "pointer" }}
        >
          ファイルを選択
        </MUILink>
        <Typography variant="subtitle1" color="text.secondary" fontWeight={400}>
          もしくはドラッグ＆ドロップ
        </Typography>
      </Stack>
      <Typography variant="caption" sx={{ color: "#7A7A7A" }}>
        .asaまたは.bin
      </Typography>
    </Stack>
  );
}

type FileUploadItemProps = {
  uploadFile: UploadFileState;
  onRemove: () => void;
};

function FileUploadItem({ uploadFile, onRemove }: FileUploadItemProps) {
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)}KB`;
    if (bytes < 1024 * 1024 * 1024)
      return `${(bytes / (1024 * 1024)).toFixed(2)}MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)}GB`;
  };

  const renderStatusText = () => {
    const fileSize = formatFileSize(uploadFile.file.size);

    if (uploadFile.status === "uploading") {
      return (
        <>
          {fileSize}
          <Box component="span" sx={{ px: "8px" }}>
            ・
          </Box>
          Loading
        </>
      );
    }
    if (uploadFile.status === "completed") {
      return (
        <>
          {fileSize}
          <Box component="span" sx={{ px: "8px" }}>
            ・
          </Box>
          Complete
        </>
      );
    }
    if (uploadFile.status === "error") {
      return uploadFile.error || "エラー";
    }
    return "エラー";
  };

  return (
    <Box
      sx={{
        border:
          uploadFile.status === "error"
            ? "1px solid #FF3A30"
            : "1px dashed #E0E0E0",
        backgroundColor:
          uploadFile.status === "error" ? "#FF3A300A" : "transparent",
        borderRadius: "4px",
        p: 4,
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing="16px"
        sx={{ pl: "16px" }}
      >
        <img
          src={
            uploadFile.status === "error"
              ? "/img/icon_file_error.svg"
              : "/img/icon_file.svg"
          }
          alt="file"
          style={{ width: 40, height: 40 }}
        />
        <Stack sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: "#282828", fontSize: "16px" }}
          >
            {uploadFile.file.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: uploadFile.status === "error" ? "error.main" : "#7A7A7A",
              fontSize: "14px",
            }}
          >
            {renderStatusText()}
          </Typography>
          {(uploadFile.status === "uploading" ||
            uploadFile.status === "completed" ||
            uploadFile.status === "error") && (
            <LinearProgress
              variant="determinate"
              value={uploadFile.progress}
              sx={{
                height: "4px",
                borderRadius: 0,
                backgroundColor:
                  uploadFile.status === "error" ? "transparent" : undefined,
                "& .MuiLinearProgress-bar": {
                  backgroundColor:
                    uploadFile.status === "error" ? "#FFB0AC" : "#0056A0",
                  transition: "none",
                },
                mt: "16px",
              }}
            />
          )}
        </Stack>
        <IconButton
          size="small"
          onClick={onRemove}
          sx={{ width: "48px", height: "48px" }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>
    </Box>
  );
}
